import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { api } from '../../api/client';
import { errorMessageKey, unwrap } from '../../api/api-error';
import { ensureOk } from '../../auth/session';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Notice } from '../../components/ui/notice';
import { useI18n } from '../../i18n/i18n';
import { INBOX_KEY, useInbox, WORKSPACES_KEY } from '../../workspaces/workspaces-api';
import { RoleBadge } from './role-badge';
import { paths } from '../../app/paths';

/** «Входящие» приглашения: принять или отклонить. Пустые — секции нет. */
export function InboxSection() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inbox = useInbox();
  const accept = useMutation({
    mutationFn: async (invitationId: string) =>
      unwrap(
        await api.POST('/api/v1/invitations/{invitationId}/accept', {
          params: { path: { invitationId } },
        }),
      ),
    onSuccess: async (accepted) => {
      await queryClient.invalidateQueries({ queryKey: INBOX_KEY });
      await queryClient.invalidateQueries({ queryKey: WORKSPACES_KEY });
      await navigate(paths.workspace(accepted.workspaceId));
    },
  });
  const decline = useMutation({
    mutationFn: async (invitationId: string) =>
      ensureOk(
        await api.POST('/api/v1/invitations/{invitationId}/decline', {
          params: { path: { invitationId } },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INBOX_KEY }),
  });
  const items = inbox.data?.items ?? [];
  if (items.length === 0 && !inbox.isError) return null;
  const error = accept.error ?? decline.error ?? inbox.error;

  return (
    <Card title={t('invitations.inbox.title')}>
      <ul className="divide-y divide-border text-sm">
        {items.map((invitation) => (
          <li key={invitation.id} className="flex items-center justify-between gap-4 py-2">
            <div className="min-w-0">
              <p className="truncate">
                <span className="font-medium">{invitation.workspace.name}</span>{' '}
                <RoleBadge role={invitation.role} />
              </p>
              <p className="text-2xs text-muted-foreground">
                {invitation.inviterName ?? t('common.formerUser')} · {t('invitations.expires')}{' '}
                {new Date(invitation.expiresAt).toLocaleDateString(locale)}
              </p>
            </div>
            <span className="flex gap-2">
              <Button
                type="button"
                disabled={accept.isPending}
                onClick={() => accept.mutate(invitation.id)}
              >
                {t('invitations.accept')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={decline.isPending}
                onClick={() => decline.mutate(invitation.id)}
              >
                {t('invitations.decline')}
              </Button>
            </span>
          </li>
        ))}
      </ul>
      {error !== null && <Notice tone="danger">{t(errorMessageKey(error))}</Notice>}
    </Card>
  );
}
